import React, { useState, useEffect } from 'react';
import T from '../../../components/common/T';
import { useParams, useNavigate } from 'react-router-dom';
import { useUsers } from '../../../context/UserContext';
import { useRoles } from '../../../context/RoleContext'; 
// import Layout from '../../../components/Layout';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserCircleIcon, EnvelopeIcon, BriefcaseIcon, UserIcon } from '@heroicons/react/24/outline';

// 🟢 SECURITY IMPORT
import { useAuth } from '../../../context/AuthContext';

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { users, updateUser } = useUsers();
  const { roles } = useRoles(); 
  const { checkAccess } = useAuth(); // 🟢 Get Checker

  // 🟢 PAGE GUARD
  useEffect(() => {
    if (!checkAccess('user.edit')) {
        toast.error("Access Denied: You do not have permission to edit users.");
        navigate('/admin/users');
    }
  }, [checkAccess, navigate]);

  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    role: '',
    status: 'Active'
  });

  useEffect(() => {
    const userToEdit = users.find(u => u.id === parseInt(id));
    
    if (userToEdit) {
      setFormData({
        name: userToEdit.name,
        username: userToEdit.username,
        email: userToEdit.email,
        role: userToEdit.role,
        status: userToEdit.status || 'Active'
      });
    } else {
      // Only show error if permission check passed (to avoid double toasts)
      if(checkAccess('user.edit')) {
        toast.error("User not found");
        setTimeout(() => navigate('/admin/users'), 1000);
      }
    }
  }, [id, users, navigate, checkAccess]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.role) {
      return toast.warn("Name, Email, and Role are required.");
    }

    updateUser(parseInt(id), formData);
    toast.success("User updated successfully!");
    setTimeout(() => navigate('/admin/users'), 1000);
  };

  if (!checkAccess('user.edit')) return null;

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen font-sans">
        <div className="max-w-4xl mx-auto mb-6 flex justify-between items-end">
          <div>
            <h2 className="text-xl font-bold text-gray-800"><T>Edit User</T></h2>
            <p className="text-gray-500 mt-1"><T>Update employee details and system access.</T></p>
          </div>
          <span className="text-sm font-mono text-gray-400 bg-gray-100 px-3 py-1 rounded">ID: {id}</span>
        </div>
        <div className="max-w-4xl mx-auto bg-white p-8 rounded-xl shadow-lg border border-gray-100">
          <form onSubmit={handleUpdate}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2 uppercase tracking-wide"><T>Full Name</T></label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserCircleIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type="text" 
                    name="name"
                    className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all bg-gray-50 focus:bg-white"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2 uppercase tracking-wide"><T>Username</T></label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type="text" 
                    name="username"
                    className="w-full pl-10 p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                    value={formData.username}
                    readOnly
                    disabled
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2 uppercase tracking-wide"><T>Email Address</T></label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type="email" 
                    name="email"
                    className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all bg-gray-50 focus:bg-white"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2 uppercase tracking-wide"><T>System Role</T></label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BriefcaseIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <select 
                    name="role"
                    className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white cursor-pointer appearance-none"
                    value={formData.role}
                    onChange={handleChange}
                  >
                    <option value="" disabled><T>Select a Role...</T></option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.name}>{role.name}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-gray-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2 uppercase tracking-wide"><T>Account Status</T></label>
                <div className="flex items-center gap-4 mt-2">
                  <label className={`flex-1 p-3 border rounded-lg cursor-pointer text-center transition-all ${formData.status === 'Active' ? 'bg-emerald-50 border-emerald-500 text-emerald-700 font-bold shadow-sm' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                    <input 
                      type="radio" 
                      name="status" 
                      value="Active" 
                      checked={formData.status === 'Active'}
                      onChange={handleChange}
                      className="hidden"
                    />
                    Active
                  </label>
                  <label className={`flex-1 p-3 border rounded-lg cursor-pointer text-center transition-all ${formData.status === 'Inactive' ? 'bg-red-50 border-red-500 text-red-700 font-bold shadow-sm' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                    <input 
                      type="radio" 
                      name="status" 
                      value="Inactive" 
                      checked={formData.status === 'Inactive'}
                      onChange={handleChange}
                      className="hidden"
                    />
                    Inactive
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-4 border-t pt-6 mt-2">
              <button 
                type="button"
                onClick={() => navigate('/admin/users')}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="px-8 py-2.5 bg-emerald-600 text-white font-bold rounded-lg hover:bg-emerald-700 transition shadow-lg transform hover:-translate-y-0.5"
              >
                Update User
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditUser;